import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ContactForm } from '../components/contact/ContactForm';
import { directContacts, InquiryType, inquiryReasons } from '../data/contact';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function ContactPage() {
  const [presetInquiry, setPresetInquiry] = useState<InquiryType>('');
  const chooseReason = (value: InquiryType) => {
    setPresetInquiry(value);
    document.getElementById('contact-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    window.setTimeout(() => document.getElementById('firstName')?.focus({
      preventScroll: true
    }), 400);
  };
  return <>
      <section className="bg-surface-bg border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-28" data-id="element-2760" data-mp="ez4ljgup77b8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-2761" data-mp="1lfgcoxz3roxb">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeOutStrong
        }} className="lg:col-span-5" data-id="element-2762" data-mp="1qcyoqx8f2k9j">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-2763" data-mp="mzla4mq7yf87">Contact</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight mb-8" data-id="element-2764" data-mp="hyxhxxq97sce">
              Get in Touch
            </h1>
            <p className="text-xl text-ink-dark leading-relaxed mb-12" data-id="element-2765" data-mp="81kgy0gq6hhr">
              We’d like to hear from people and organizations interested in
              knowledge infrastructure, research collaboration, Noetivra’s
              interface and modules, human capability frameworks, or
              institutional pilots.
            </p>
            <p className="text-ink-muted leading-relaxed -mt-8 mb-12" data-id="element-2766" data-mp="3qhd589znjfam">
              Prefer to explore first?{' '}
              <a href="/noetivra/demo-center" className="font-semibold text-navy hover:text-navy-light underline underline-offset-4 transition-colors duration-150" data-id="element-2767" data-mp="3lfxhga63auoj">
                Try the seven self-guided demos
              </a>{' '}
              in the Demo Center, then get in touch to discuss an SKP demonstration,
              framework research or a Noetivra pilot.
            </p>

            <dl className="border-t border-line" data-id="element-2768" data-mp="2xuwps1vbpbnl">
              {directContacts.map(contact => <div key={contact.email} className="border-b border-line py-5" data-id="element-2769" data-mp="31v74c2x6lae2">
                  <dt className="text-sm text-ink-muted mb-1" data-id="element-2770" data-mp="3plsxyp6fus4j">{contact.label}</dt>
                  <dd data-id="element-2771" data-mp="3ujuizfnfd1in">
                    <a href={`mailto:${contact.email}`} className="font-heading font-bold text-lg text-navy hover:text-navy-light transition-colors duration-150" data-id="element-2772" data-mp="3uj2yazvd8o75">
                      {contact.email}
                    </a>
                  </dd>
                </div>)}
              <div className="py-5" data-id="element-2773" data-mp="h4g2cn26tbna">
                <dt className="text-sm text-ink-muted mb-1" data-id="element-2774" data-mp="h5hq9s4axsmf">Response time</dt>
                <dd className="text-ink-dark leading-relaxed" data-id="element-2775" data-mp="21ubp7axq95bg">
                  We typically respond to inquiries within 2–3 business days.
                  For urgent matters, please indicate that in your message.
                </dd>
              </div>
            </dl>
          </motion.div>

          <motion.div id="contact-form" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.06,
          ease: easeOutStrong
        }} className="lg:col-span-7 scroll-mt-28" data-id="element-2776" data-mp="26laa6ntnurl1">
            <ContactForm presetInquiry={presetInquiry} data-id="element-2777" data-mp="1suf41jajqol5" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-id="element-2778" data-mp="1nvlqxrz68zli">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2779" data-mp="1nu0ws3grrio6">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12" data-id="element-2780" data-mp="348so0d26ijvw">
            <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-2781" data-mp="2z9fu58cw4cjt">
              Reasons to reach out
            </h2>
            <p className="lg:col-span-7 text-xl text-ink-muted leading-relaxed lg:pt-2" data-id="element-2782" data-mp="1drym1joeeabz">
              Whether you’re exploring a partnership, requesting a demo, or have
              questions about our research, we’d like to hear from you. Choose a
              topic to start your message.
            </p>
          </motion.div>

          <motion.ul {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 border-t border-line" data-id="element-2783" data-mp="1rrq6o39f6oyy">
            {inquiryReasons.map(reason => <li key={reason.value} className="border-b border-line" data-id="element-2784" data-mp="21hsvrsdf7is0">
                <button type="button" onClick={() => chooseReason(reason.value)} className="group flex w-full h-full items-start gap-4 py-7 text-left" data-id="element-2785" data-mp="25jmsxqoeq4nf">
                  <span className="flex-1" data-id="element-2786" data-mp="2fh0zmykj3f2j">
                    <span className="block font-heading font-bold text-lg text-ink-dark group-hover:text-navy transition-colors duration-150" data-id="element-2787" data-mp="2fi3ol0yaj7ft">
                      {reason.title}
                    </span>
                    <span className="mt-1.5 block text-ink-muted leading-relaxed" data-id="element-2788" data-mp="2kor0z91uq8n1">{reason.description}</span>
                  </span>
                  <ArrowRight size={18} aria-hidden="true" className="mt-1 shrink-0 text-teal transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2789" data-mp="2bm9ectiabdzi" />
                </button>
              </li>)}
          </motion.ul>
        </div>
      </section>
    </>;
}